import * as React from 'react';
import axios from 'axios';
import { TeamMemberWorkload, TeamWorkloadApiMember, WorkloadStatus } from './types';
import {
  Brand,
  Capacity,
  CapacityButton,
  CapacityEditor,
  CapacityError,
  CapacityInput,
  CapacityLabels,
  CapacityLimitButton,
  CapacityPercent,
  Chip,
  Content,
  DashboardPage,
  Fill,
  Header,
  HeadingGroup,
  Identity,
  Legend,
  LegendItem,
  Logo,
  MemberCard,
  MemberName,
  MemberTop,
  MetricCard,
  Metrics,
  palette,
  SlotsLeft,
  SortNote,
  SummaryRow,
  TeamList,
  Track,
  WorkloadChips,
} from './styled';

const statusDetails: Record<WorkloadStatus, { label: string; color: string }> = {
  available: { label: 'Available', color: palette.green },
  'near-capacity': { label: 'Near Capacity', color: palette.orange },
  full: { label: 'Full', color: palette.red },
};

const WorkloadChip = ({ icon, label, value }: { icon: string; label: string; value: number }) => (
  <Chip dimmed={value === 0}>
    <span aria-hidden="true">{icon}</span>
    {label}: <strong>{value}</strong>
  </Chip>
);

const getWorkloadStatus = (percent: number): WorkloadStatus => {
  if (percent >= 100) return 'full';
  if (percent >= 85) return 'near-capacity';
  return 'available';
};

const getCapacityPercent = (assigned: number, capacity: number) => {
  if (capacity <= 0) return 100;

  return Math.round((assigned / capacity) * 100);
};

interface MemberWorkloadCardProps {
  member: TeamMemberWorkload;
  onSaveCapacity: (userId: number, capacity: number) => Promise<void>;
}

const MemberWorkloadCard = ({ member, onSaveCapacity }: MemberWorkloadCardProps) => {
  const [editingCapacity, setEditingCapacity] = React.useState(false);
  const [draftCapacity, setDraftCapacity] = React.useState(String(member.capacityLimit));
  const [savingCapacity, setSavingCapacity] = React.useState(false);
  const [capacityError, setCapacityError] = React.useState<string | null>(null);
  const percent = getCapacityPercent(member.assigned, member.capacityLimit);
  const detail = statusDetails[getWorkloadStatus(percent)];

  const startEditingCapacity = () => {
    setDraftCapacity(String(member.capacityLimit));
    setCapacityError(null);
    setEditingCapacity(true);
  };

  const cancelEditingCapacity = () => {
    setCapacityError(null);
    setEditingCapacity(false);
  };

  const saveCapacity = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const capacity = Number(draftCapacity);

    if (draftCapacity.trim() === '' || !Number.isInteger(capacity) || capacity < 0) {
      setCapacityError('Enter a whole number of 0 or more.');
      return;
    }

    try {
      setSavingCapacity(true);
      setCapacityError(null);
      await onSaveCapacity(member.id, capacity);
      setEditingCapacity(false);
    } catch {
      setCapacityError('Unable to save capacity.');
    } finally {
      setSavingCapacity(false);
    }
  };

  return (
    <MemberCard>
      <MemberTop>
        <Identity>
          <MemberName
            aria-label={`View assigned user report for ${member.name}`}
            rel="noopener noreferrer"
            target="_blank"
            to={`/reports/types/assignedUser?initialEmployeeId=${member.id}`}
          >
            {member.name}
          </MemberName>
        </Identity>

        <Capacity>
          <CapacityLabels>
            <span>
              {member.assigned} {member.assigned === 1 ? 'order' : 'orders'} assigned
              {' · '}
              {member.totalSearches} {member.totalSearches === 1 ? 'search' : 'searches'} total
              {member.waitingOnSearches > 0 && (
                <>
                  {' · '}
                  {member.waitingOnSearches} {member.waitingOnSearches === 1 ? 'search' : 'searches'} in waiting
                </>
              )}
            </span>
            {editingCapacity ? (
              <CapacityEditor onSubmit={saveCapacity}>
                <CapacityInput
                  aria-label={`Capacity for ${member.name}`}
                  autoFocus
                  min="0"
                  step="1"
                  type="number"
                  value={draftCapacity}
                  onChange={event => setDraftCapacity(event.target.value)}
                />
                <CapacityButton disabled={savingCapacity} type="submit">
                  Save
                </CapacityButton>
                <CapacityButton disabled={savingCapacity} type="button" onClick={cancelEditingCapacity}>
                  Cancel
                </CapacityButton>
              </CapacityEditor>
            ) : (
              <span>
                {member.limitLabel || `Limit: ${member.capacityLimit}`}
                <CapacityLimitButton type="button" onClick={startEditingCapacity}>
                  Edit
                </CapacityLimitButton>
              </span>
            )}
          </CapacityLabels>
          {capacityError && <CapacityError>{capacityError}</CapacityError>}
          <Track>
            <Fill percent={percent} fillColor={detail.color} />
          </Track>
          <CapacityPercent>
            {member.capacityLimit <= 0 ? 'No capacity available' : `${percent}% of capacity`}
          </CapacityPercent>
        </Capacity>

        <SlotsLeft statusColor={detail.color}>
          <strong>{member.slotsOpen}</strong>
          <span>{member.slotsOpen === 1 ? 'slot left' : 'slots left'}</span>
        </SlotsLeft>
      </MemberTop>

      <WorkloadChips>
        <WorkloadChip icon="🔄" label="Contins" value={member.workload.contins} />
        <WorkloadChip icon="🧾" label="Tax" value={member.workload.tax} />
        <WorkloadChip icon="🏛️" label="Municipal" value={member.workload.municipal} />
        <WorkloadChip icon="📌" label="Other" value={member.workload.other} />
      </WorkloadChips>
    </MemberCard>
  );
};

const Dashboard = () => {
  const [teamWorkloads, setTeamWorkloads] = React.useState<TeamMemberWorkload[]>([]);
  const [dashboardLastUpdated, setDashboardLastUpdated] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    axios
      .get('/api/reports/teamworkload')
      .then(({ data }) => {
        const members = (data.members as TeamWorkloadApiMember[]).map(member => ({
          id: member.id,
          name: `${member.firstName} ${member.lastName}`.trim(),
          assigned: member.assigned,
          totalSearches: member.totalSearches,
          waitingOnSearches: member.waitingOnSearches,
          capacityLimit: member.capacityLimit,
          slotsOpen: Math.max(member.capacityLimit - member.assigned, 0),
          workload: {
            contins: member.contins,
            tax: member.tax,
            municipal: member.municipal,
            other: member.other,
          },
        }));

        setTeamWorkloads(members);
        setDashboardLastUpdated(data.generatedAt);
        setError(null);
      })
      .catch(() => setError('Unable to load the team workload dashboard.'))
      .finally(() => setLoading(false));
  }, []);

  const sortedTeamWorkloads = [...teamWorkloads].sort(
    (a, b) => b.slotsOpen - a.slotsOpen || a.name.localeCompare(b.name)
  );
  const totalSlotsOpen = teamWorkloads.reduce((total, member) => total + member.slotsOpen, 0);
  const freelyAvailable = teamWorkloads.filter(member => {
    const percent = getCapacityPercent(member.assigned, member.capacityLimit);
    return getWorkloadStatus(percent) === 'available';
  }).length;

  const handleSaveCapacity = async (userId: number, capacity: number) => {
    await axios.put(`/api/reports/teamworkload/${userId}/capacity`, { capacity });
    setTeamWorkloads(current =>
      current.map(member =>
        member.id === userId
          ? { ...member, capacityLimit: capacity, slotsOpen: Math.max(capacity - member.assigned, 0) }
          : member
      )
    );
    setDashboardLastUpdated(new Date().toISOString());
  };

  return (
    <DashboardPage>
      <Header>
        <Brand aria-label="Hudson Search">
          <Logo
            src="https://res.cloudinary.com/dyuuf6gor/image/upload/v1765985214/hudson/hudson_logo-3_oekf2k.png"
            alt="Hudson Search"
          />
        </Brand>
        <HeadingGroup>
          <h1>TEAM WORKLOAD DASHBOARD</h1>
          <p>
            Last updated:{' '}
            {dashboardLastUpdated ? new Date(dashboardLastUpdated).toLocaleString() : 'Loading...'}
          </p>
        </HeadingGroup>
      </Header>

      <Content>
        {loading && <div>Loading team workload...</div>}
        {error && <div>{error}</div>}
        {!loading && !error && !teamWorkloads.length && <div>No team workload data is available.</div>}
        <SummaryRow>
          <SortNote>Sorted by most available capacity</SortNote>
          <Legend>
            <LegendItem color={palette.green}>Available</LegendItem>
            <LegendItem color={palette.orange}>Near Capacity</LegendItem>
            <LegendItem color={palette.red}>Full</LegendItem>
          </Legend>
          <Metrics>
            <MetricCard>
              <strong>{totalSlotsOpen}</strong>
              <span>Total Slots Open</span>
            </MetricCard>
            <MetricCard>
              <strong>
                {freelyAvailable}/{teamWorkloads.length}
              </strong>
              <span>Freely Available</span>
            </MetricCard>
          </Metrics>
        </SummaryRow>

        <TeamList>
          {sortedTeamWorkloads.map(member => (
            <MemberWorkloadCard
              key={member.id}
              member={member}
              onSaveCapacity={handleSaveCapacity}
            />
          ))}
        </TeamList>
      </Content>
    </DashboardPage>
  );
};

export default Dashboard;
